/* eslint-disable react/prop-types */
import React from 'react';
import { format, parse } from 'date-fns';
import {
  CheckCircle,
  LockRounded,
  TimelapseRounded
} from '@mui/icons-material';
import CertificationBadge from './CertificationBadge';
import ShareActions from './ShareActions';
import { getCertificationDisplayName } from '../../constants/certificationBadgeCopy';

const BADGE_THEME = {
  delivers_with_impact: {
    outer: '#C68A2B',
    mid: '#F6E6BF',
    inner: '#FFFDF7',
    ribbonDark: '#9A6A1A'
  },
  delivers_with_precision: {
    outer: '#4545C8',
    mid: '#DDDDFE',
    inner: '#F5F5FF',
    ribbonDark: '#2C2C9A'
  },
  built_to_last: {
    outer: '#0D7A56',
    mid: '#C8EEE0',
    inner: '#F0FBF6',
    ribbonDark: '#085A3E'
  }
};

const getTheme = badge =>
  BADGE_THEME[badge?.badge_key] || BADGE_THEME.delivers_with_impact;

const getStatusMeta = status => {
  if (status === 'active') {
    return {
      label: 'Earned',
      icon: CheckCircle,
      text: '#166534',
      border: '#BBF7D0',
      background:
        'linear-gradient(180deg, rgba(240,253,244,0.96), rgba(220,252,231,0.92))'
    };
  }

  if (status === 'expired') {
    return {
      label: 'Expired',
      icon: TimelapseRounded,
      text: '#B45309',
      border: '#FDE68A',
      background:
        'linear-gradient(180deg, rgba(255,251,235,0.96), rgba(254,243,199,0.92))'
    };
  }

  return {
    label: 'Locked',
    icon: LockRounded,
    text: '#475569',
    border: '#CBD5E1',
    background:
      'linear-gradient(180deg, rgba(248,250,252,0.98), rgba(226,232,240,0.92))'
  };
};

const formatValidThrough = validThrough => {
  if (!validThrough) return 'Available after earning.';
  try {
    return `Valid through ${format(
      parse(validThrough, 'yyyy-MM', new Date()),
      'MMMM yyyy'
    )}.`;
  } catch {
    return `Valid through ${validThrough}.`;
  }
};

const CertificationCard = ({
  badge,
  companyName,
  onCopyVerificationLink,
  onEmbedBadge,
  onDownloadPreview
}) => {
  const displayName = getCertificationDisplayName(badge);
  const theme = getTheme(badge);
  const isEarned = badge?.status === 'active';
  const isLocked = badge?.status === 'not_earned';
  const validThroughLabel = formatValidThrough(
    badge?.valid_through_end_year_month
  );
  const statusMeta = getStatusMeta(badge?.status);
  const StatusIcon = statusMeta.icon;

  return (
    <article
      className={`group relative overflow-hidden rounded-3xl border bg-white transition-all duration-300 ${
        isEarned
          ? 'border-slate-200 shadow-lg hover:shadow-xl hover:-translate-y-1.5'
          : 'border-slate-100 shadow-sm'
      }`}
      aria-labelledby={`certification-title-${badge?.badge_key}`}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-36 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.92),_rgba(255,255,255,0))]" />

      <div
        className="relative flex flex-col items-center justify-center px-0 pb-6 pt-5 sm:px-1"
        style={{
          background: isLocked
            ? 'linear-gradient(160deg, #f8fafc 0%, #f1f5f9 100%)'
            : `radial-gradient(circle at 50% 12%, ${theme.mid}CC 0%, ${theme.inner} 40%, #ffffff 78%), linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)`
        }}
      >
        {!isLocked && (
          <div
            className="pointer-events-none absolute inset-x-8 top-8 h-32 rounded-full blur-3xl"
            style={{
              background: `radial-gradient(circle, ${theme.outer}26 0%, transparent 72%)`
            }}
          />
        )}

        {!isLocked && (
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='32'%3E%3Cpath d='M14 2 L26 9 L26 23 L14 30 L2 23 L2 9 Z' fill='none' stroke='${encodeURIComponent(theme.outer)}' stroke-width='1'/%3E%3C/svg%3E")`,
              backgroundSize: '28px 32px'
            }}
          />
        )}

        <div className="relative z-10 flex w-full justify-start px-4">
          <div
            className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-bold shadow-sm backdrop-blur-sm"
            style={{
              color: statusMeta.text,
              borderColor: statusMeta.border,
              background: statusMeta.background
            }}
          >
            <StatusIcon sx={{ fontSize: 18 }} />
            <span>{statusMeta.label}</span>
          </div>
        </div>

        <div
          id={`preview-${badge?.badge_key}`}
          className="relative z-10 w-full"
        >
          <CertificationBadge badge={badge} companyName={companyName} />
        </div>
      </div>

      <div
        className="h-[2px] w-full"
        style={{
          background: isLocked
            ? '#e2e8f0'
            : `linear-gradient(90deg, transparent, ${theme.outer}66, transparent)`
        }}
      />

      <div className="space-y-4 px-5 py-5">
        <div className="space-y-1 text-center">
          <p
            id={`certification-title-${badge?.badge_key}`}
            className="text-base font-bold tracking-tight"
            style={{ color: isLocked ? '#94a3b8' : theme.ribbonDark }}
          >
            {displayName}
          </p>
          <p className="text-xs text-slate-400">{validThroughLabel}</p>
        </div>

        <ShareActions
          badge={{ ...badge, display_name: displayName }}
          onCopyVerificationLink={onCopyVerificationLink}
          onEmbedBadge={onEmbedBadge}
          onDownloadPreview={onDownloadPreview}
        />
      </div>
    </article>
  );
};

export default CertificationCard;
